/**
 * Opaque keys for people, so a shared link or a saved view never carries a name.
 *
 * A link names people — the Employee filter, a drill-through, a team — but this app is
 * individual pay, and a URL ends up in browser history, proxy logs, chat messages and
 * Asana tasks. So in anything that leaves the browser tab, a person is a key: a short
 * hash of their FCCKey (of their name, on a bundle built before FCCKeys were carried).
 *
 * Why a hash, not an ID the bundle already holds:
 *  - The Paylocity ID is shown in the app (#373) and is what HR and payroll know a
 *    person by. In a URL it identifies them to anyone with Paylocity, without the app.
 *  - The FCCKey is what the API authorizes on (api/shared/scope.ts). It is no secret
 *    from someone holding the bundle, but nothing outside the app needs to see it.
 *  - A hash is meaningless without the bundle, and the bundle already holds the name.
 *    Opaque, not secret: anyone served the person can compute it; that is the point —
 *    it is how the receiving browser finds them.
 *
 * The receiving user's scope still applies, and needs nothing extra: their bundle is
 * cut to their grant by the API, so a key for someone outside it simply does not
 * resolve. The app says "not in your access" and shows nothing of that person.
 */
import type { EmployeeRow } from '@/data/types'

/** Bump the salt to invalidate every key ever issued (old links then refuse people). */
const SALT = 'cpc-person-1:'

/**
 * cyrb53: a small, fast, well-mixed 53-bit string hash. Not cryptographic, and it does
 * not need to be — see the header. Synchronous, so a render can use it.
 */
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return 4294967296 * (2097151 & h2) + (h1 >>> 0)
}

/** A person's key: 10 base-36 characters. */
export function personKey(p: Pick<EmployeeRow, 'employee'> & { fccKey?: string | null }): string {
  const basis = p.fccKey ? `k:${p.fccKey}` : `n:${p.employee}`
  return cyrb53(SALT + basis).toString(36).padStart(10, '0').slice(-10)
}

/** Every person the caller is served, both ways. */
export interface PeopleKeys {
  toKey: (name: string) => string | null
  toName: (key: string) => string | null
  /** Every name, longest first: for finding names inside a label. */
  names: readonly string[]
}

export function buildPeopleKeys(
  rows: readonly (Pick<EmployeeRow, 'employee'> & { fccKey?: string | null })[],
): PeopleKeys {
  const byName = new Map<string, string>()
  const byKey = new Map<string, string>()
  for (const r of rows) {
    if (!r.employee || byName.has(r.employee)) continue
    const k = personKey(r)
    byName.set(r.employee, k)
    byKey.set(k, r.employee)
  }
  return {
    toKey: (n) => byName.get(n) ?? null,
    toName: (k) => byKey.get(k) ?? null,
    names: [...byName.keys()].sort((a, b) => b.length - a.length),
  }
}

export const NO_PEOPLE: PeopleKeys = buildPeopleKeys([])

/*
 * The current caller's keys, for code outside React's tree of providers (the history
 * hooks, the link builder). Set by <ViewLinks /> whenever the bundle changes.
 */
let current: PeopleKeys = NO_PEOPLE
const listeners = new Set<() => void>()

export function setPeopleKeys(keys: PeopleKeys): void {
  current = keys
  for (const l of listeners) l()
}

export const peopleKeys = (): PeopleKeys => current

export function subscribePeopleKeys(l: () => void): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}
